import {
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  computed,
  Injectable,
  Injector,
  signal,
} from '@angular/core';
import type { AiChatWindowData } from '../components/ai-chat-window/ai-chat-window.component';
import {
  AI_CHAT_WINDOW_DATA,
  AiChatWindowComponent,
} from '../components/ai-chat-window/ai-chat-window.component';

export type AiChatWindowState = 'normal' | 'min' | 'max';

interface WindowRef {
  id: string;
  overlayRef: OverlayRef;
  strategy: GlobalPositionStrategy;
  componentRef: ComponentRef<AiChatWindowComponent>;
  state: AiChatWindowState;
  normalSize: { width: number; height: number };
  position: { x: number; y: number };
  normalPosition: { x: number; y: number };
}

@Injectable({ providedIn: 'root' })
export class AiChatWindowService {
  private readonly gap = 16;
  private readonly baseSize = { width: 400, height: 520 };
  private readonly minHeaderHeight = 40;
  private readonly maxCap = { width: 1440, height: 960 };
  private readonly normalLift = 12;

  private windows: WindowRef[] = [];

  // Signals for reactive state management
  private readonly windowsSignal = signal<WindowRef[]>([]);
  readonly windows$ = computed(() => this.windowsSignal());

  readonly hasOpenWindows = computed(() => this.windowsSignal().length > 0);
  readonly openWindowCount = computed(() => this.windowsSignal().length);

  // Signal-based methods for reactive access
  getWindowById(id: string) {
    return computed(() => this.windowsSignal().find(w => w.id === id));
  }

  getWindowState(id: string) {
    return computed(() => {
      const window = this.windowsSignal().find(w => w.id === id);
      return window?.state ?? null;
    });
  }

  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector
  ) {}

  openChat(config: Partial<AiChatWindowData> = {}): string {
    const existing = this.windows[0];
    if (existing) {
      if (existing.state === 'min') {
        existing.state = 'normal';
        this.applyState(existing);
      }
      return existing.id;
    }

    const id = `aiw_${Date.now()}`;
    const normalSize = {
      width: config.width ?? this.baseSize.width,
      height: config.height ?? this.baseSize.height,
    };

    const windowData: AiChatWindowData = {
      id,
      title: config.title ?? 'AI Chat',
      width: normalSize.width,
      height: normalSize.height,
    };

    const strategy = this.overlay.position().global();
    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      disposeOnNavigation: false,
      positionStrategy: strategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const injector = Injector.create({
      parent: this.injector,
      providers: [{ provide: AI_CHAT_WINDOW_DATA, useValue: windowData }],
    });

    const componentRef = overlayRef.attach(
      new ComponentPortal(AiChatWindowComponent, null, injector)
    );

    const startPos = this.findInitialPositionAvoidingFab(normalSize);
    overlayRef.updateSize({
      width: normalSize.width,
      height: normalSize.height,
    });
    this.lockPaneSize(overlayRef, normalSize);

    strategy.left(`${startPos.x}px`).top(`${startPos.y}px`);
    overlayRef.updatePosition();

    componentRef.instance.setState('normal');
    componentRef.instance.setPosition(startPos);

    const newWindow: WindowRef = {
      id,
      overlayRef,
      strategy,
      componentRef,
      state: 'normal',
      normalSize,
      position: startPos,
      normalPosition: startPos,
    };

    this.windows = [newWindow];
    this.windowsSignal.set([newWindow]);

    overlayRef.detachments().subscribe(() => this.removeIfExists(id));
    return id;
  }

  close(id: string): void {
    const ref = this.lookup(id);
    if (!ref) return;
    ref.overlayRef.dispose();
    this.removeIfExists(id);
  }

  toggleMinimize(id: string): void {
    const ref = this.lookup(id);
    if (!ref) return;
    ref.state = ref.state === 'min' ? 'normal' : 'min';
    this.applyState(ref);
  }

  toggleMaximize(id: string): void {
    const ref = this.lookup(id);
    if (!ref) return;
    ref.state = ref.state === 'max' ? 'normal' : 'max';
    this.applyState(ref);
  }

  updatePosition(
    id: string,
    pos: { x: number; y: number }
  ): { x: number; y: number } {
    const ref = this.lookup(id);
    if (!ref) return pos;
    if (ref.state === 'max') return ref.position;

    const width = ref.normalSize.width;
    const height =
      ref.state === 'min' ? this.minHeaderHeight : ref.normalSize.height;

    const target =
      ref.state === 'min'
        ? { x: pos.x, y: window.innerHeight - height - this.gap }
        : pos;

    const clamped = this.clamp(target, width, height);
    ref.position = clamped;
    if (ref.state === 'normal') ref.normalPosition = clamped;

    this.applyPosition(ref, clamped);
    return clamped;
  }

  // ---------- helpers ----------

  private lookup(id: string): WindowRef | undefined {
    return this.windows.find(w => w.id === id);
  }

  private removeIfExists(id: string): void {
    const idx = this.windows.findIndex(w => w.id === id);
    if (idx >= 0) {
      this.windows.splice(idx, 1);
      this.windowsSignal.set([...this.windows]);
    }
  }

  private applyState(ref: WindowRef): void {
    switch (ref.state) {
      case 'min': {
        const size = {
          width: ref.normalSize.width,
          height: this.minHeaderHeight,
        };
        ref.overlayRef.updateSize({ width: size.width, height: size.height });
        this.lockPaneSize(ref.overlayRef, size);

        let pos = this.clamp(
          { x: ref.position.x, y: window.innerHeight - size.height - this.gap },
          size.width,
          size.height
        );
        pos = this.avoidLauncher(pos, size);

        ref.position = pos;
        this.applyPosition(ref, pos);
        ref.componentRef.instance.setState('min');
        ref.componentRef.instance.setPosition(pos);
        break;
      }
      case 'max': {
        const size = this.computeMaxSize();
        ref.overlayRef.updateSize({ width: size.width, height: size.height });
        this.lockPaneSize(ref.overlayRef, size);
        const pos = this.center(size);
        ref.position = pos;
        this.applyPosition(ref, pos);
        ref.componentRef.instance.setState('max');
        ref.componentRef.instance.setPosition(pos);
        break;
      }
      default: {
        const size = ref.normalSize;
        ref.overlayRef.updateSize({ width: size.width, height: size.height });
        this.lockPaneSize(ref.overlayRef, size);

        let pos = this.clamp(ref.normalPosition, size.width, size.height);

        pos = { x: pos.x, y: Math.max(this.gap, pos.y - this.normalLift) };

        ref.position = pos;
        this.applyPosition(ref, pos);
        ref.componentRef.instance.setState('normal');
        ref.componentRef.instance.setPosition(pos);
        break;
      }
    }
  }

  private applyPosition(ref: WindowRef, pos: { x: number; y: number }): void {
    ref.strategy.left(`${pos.x}px`).top(`${pos.y}px`);
    ref.overlayRef.updatePosition();
  }

  private findInitialPositionAvoidingFab(size: {
    width: number;
    height: number;
  }) {
    let pos = this.clamp(
      {
        x: window.innerWidth - size.width - this.gap,
        y: window.innerHeight - size.height - this.gap,
      },
      size.width,
      size.height
    );

    pos = this.avoidLauncher(pos, size);

    pos = { x: pos.x, y: Math.max(this.gap, pos.y - this.normalLift) };
    return pos;
  }

  private computeMaxSize() {
    const width = Math.max(
      this.baseSize.width,
      Math.min(Math.floor(window.innerWidth * 0.9), this.maxCap.width)
    );
    const height = Math.max(
      this.baseSize.height,
      Math.min(Math.floor(window.innerHeight * 0.9), this.maxCap.height)
    );
    return { width, height };
  }

  private center(size: { width: number; height: number }) {
    const x = Math.max(
      this.gap,
      Math.floor((window.innerWidth - size.width) / 2)
    );
    const y = Math.max(
      this.gap,
      Math.floor((window.innerHeight - size.height) / 2)
    );
    return this.clamp({ x, y }, size.width, size.height);
  }

  private clamp(pos: { x: number; y: number }, width: number, height: number) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minX = this.gap;
    const minY = this.gap;
    const maxX = Math.max(this.gap, vw - width - this.gap);
    const maxY = Math.max(this.gap, vh - height - this.gap);
    return {
      x: Math.min(Math.max(minX, pos.x), maxX),
      y: Math.min(Math.max(minY, pos.y), maxY),
    };
  }

  private lockPaneSize(
    overlayRef: OverlayRef,
    size: { width: number; height: number }
  ): void {
    const pane = overlayRef.overlayElement as HTMLElement;
    pane.style.boxSizing = 'border-box';
    pane.style.width = `${size.width}px`;
    pane.style.height = `${size.height}px`;
    pane.style.minWidth = `${size.width}px`;
    pane.style.minHeight = `${size.height}px`;
    pane.style.maxWidth = 'none';
    pane.style.maxHeight = 'none';

    const child = pane.firstElementChild as HTMLElement | null;
    if (child) {
      child.style.width = '100%';
      child.style.height = '100%';
    }
  }

  private getLauncherRect(): DOMRect | null {
    const el =
      (document.querySelector('.qt-ai-fab') as HTMLElement) ||
      (document.querySelector('.fab-add') as HTMLElement) ||
      null;
    return el ? el.getBoundingClientRect() : null;
  }

  private avoidLauncher(
    pos: { x: number; y: number },
    size: { width: number; height: number }
  ): { x: number; y: number } {
    const fab = this.getLauncherRect();
    if (!fab) return pos;

    const pad = 12;
    const overlaps = (p: { x: number; y: number }) => {
      const x1 = p.x;
      const y1 = p.y;
      const x2 = p.x + size.width;
      const y2 = p.y + size.height;
      return !(
        x2 < fab.left - pad ||
        x1 > fab.right + pad ||
        y2 < fab.top - pad ||
        y1 > fab.bottom + pad
      );
    };

    if (!overlaps(pos)) return pos;

    const bottomY = Math.max(
      this.gap,
      window.innerHeight - size.height - this.gap
    );
    const leftOfFab = this.clamp(
      { x: Math.floor(fab.left - size.width - this.gap), y: bottomY },
      size.width,
      size.height
    );
    if (!overlaps(leftOfFab)) return leftOfFab;

    const aboveFab = this.clamp(
      {
        x: Math.max(this.gap, window.innerWidth - size.width - this.gap),
        y: Math.floor(fab.top - size.height - this.gap),
      },
      size.width,
      size.height
    );
    if (!overlaps(aboveFab)) return aboveFab;

    return this.clamp(pos, size.width, size.height);
  }
}
